import React, { useState, useEffect } from 'react';
import './RequestsManagement.css';
import { API_URL } from '../config/api';

const RequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState([]); // Panier multi-articles
  const [alert, setAlert] = useState(null);
  const [userScans, setUserScans] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Recharger les items quand une demande est sélectionnée pour avoir le stock à jour
  useEffect(() => {
    if (selectedRequest) {
      fetchItems();
    }
  }, [selectedRequest]);

  const fetchRequests = async () => {
    try {
      const url = filter === 'all' 
        ? `${API_URL}/api/requests`
        : `${API_URL}/api/requests?status=${filter}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setRequests(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error);
      setLoading(false);
    }
  };

  const fetchItems = async (showAll = false) => {
    try {
      // Ajouter un timestamp pour éviter le cache
      const response = await fetch(`${API_URL}/api/items?t=${Date.now()}`);
      const data = await response.json();
      // Filtrer uniquement les éléments en stock (sauf si showAll est true)
      const availableItems = showAll ? data : data.filter(item => item.stock_quantity > 0);
      setItems(availableItems);
    } catch (error) {
      console.error('Erreur lors du chargement des éléments:', error);
    }
  };

  const fetchUserScans = async (userId) => {
    if (!userId) {
      setUserScans([]);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/scans/user/${userId}`);
      const data = await response.json();
      setUserScans(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      setUserScans([]);
    }
  };

  // Ajouter un article au panier
  const addToCart = () => {
    if (!selectedItemId) {
      showAlert('⚠️ Sélectionnez un élément', 'error');
      return;
    }
    const item = items.find(i => i.id === parseInt(selectedItemId));
    if (!item) return;

    // Verifier si l'article est deja dans le panier
    const existingIndex = cart.findIndex(c => c.item_id === parseInt(selectedItemId));
    if (existingIndex >= 0) {
      // Incrementer la quantite
      const newCart = [...cart];
      const newQty = newCart[existingIndex].quantity + parseInt(quantity);
      if (newQty > item.stock_quantity) {
        showAlert(`⚠️ Stock insuffisant. Maximum: ${item.stock_quantity}`, 'error');
        return;
      }
      newCart[existingIndex].quantity = newQty;
      setCart(newCart);
    } else {
      if (parseInt(quantity) > item.stock_quantity) {
        showAlert(`⚠️ Stock insuffisant. Maximum: ${item.stock_quantity}`, 'error');
        return;
      }
      setCart([...cart, {
        item_id: item.id,
        item_name: item.name,
        item_size: item.size,
        quantity: parseInt(quantity),
        stock: item.stock_quantity
      }]);
    }
    setSelectedItemId('');
    setQuantity(1);
  };

  // Retirer un article du panier
  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const handleApprove = async (requestId) => {
    // Si le panier est vide mais un article est selectionne, l'ajouter d'abord
    let finalCart = [...cart];
    if (finalCart.length === 0 && selectedItemId) {
      const item = items.find(i => i.id === parseInt(selectedItemId));
      if (item) {
        finalCart = [{ item_id: item.id, item_name: item.name, item_size: item.size, quantity: parseInt(quantity), stock: item.stock_quantity }];
      }
    }

    if (finalCart.length === 0) {
      showAlert('⚠️ Ajoutez au moins un élément', 'error');
      return;
    }

    if (!selectedRequest.user_name || selectedRequest.user_name.trim() === '') {
      showAlert('⚠️ Veuillez identifier l\'utilisateur', 'error');
      return;
    }

    setProcessingRequest(true);
    try {
      // Mettre a jour le nom de l'utilisateur
      await fetch(`${API_URL}/api/users/${selectedRequest.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: selectedRequest.user_name, email: selectedRequest.user_email || null }),
      });

      // Approuver avec tous les articles du panier
      const response = await fetch(`${API_URL}/api/requests/${requestId}/approve-multi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: finalCart.map(c => ({ item_id: c.item_id, quantity: c.quantity }))
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const totalQty = finalCart.reduce((sum, c) => sum + c.quantity, 0);
        const itemNames = finalCart.map(c => `${c.quantity}× ${c.item_name}${c.item_size ? ` (${c.item_size})` : ''}`).join(', ');
        showAlert(`✅ Demande traitée ! ${itemNames}`, 'success');

        setSelectedRequest(null);
        setSelectedItemId('');
        setQuantity(1);
        setCart([]);
        setUserScans([]);
        setShowHistory(false);
        await Promise.all([fetchRequests(), fetchItems()]);
      } else {
        const msg = data.message || data.error || 'Erreur lors de l\'approbation';
        showAlert(`⛔ ${msg}`, 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showAlert('❌ Erreur de connexion', 'error');
    } finally {
      setProcessingRequest(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir refuser cette demande ?')) {
      return;
    }

    setProcessingRequest(true);
    try {
      const response = await fetch(`${API_URL}/api/requests/${requestId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: 'Refusé par l\'administrateur' }),
      });

      if (response.ok) {
        showAlert('✅ Demande refusée', 'success');
      } else {
        const data = await response.json();
        // Si "deja traitee", c'est pas une vraie erreur
        if (response.status === 400) {
          showAlert('✅ Demande déjà traitée', 'success');
        } else {
          showAlert(`❌ ${data.error || 'Erreur lors du refus'}`, 'error');
        }
      }

      // Dans tous les cas : fermer le panneau et rafraichir
      setSelectedRequest(null);
      setShowHistory(false);
      await Promise.all([fetchRequests(), fetchItems()]);
    } catch (error) {
      console.error('Erreur:', error);
      showAlert('❌ Erreur de connexion', 'error');
    } finally {
      setProcessingRequest(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'status-pending', text: 'En attente' },
      approved: { class: 'status-approved', text: 'Approuvée' },
      rejected: { class: 'status-rejected', text: 'Refusée' }
    };
    const badge = badges[status] || { class: '', text: status };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="requests-management">
      <div className="page-header">
        <h1>Gestion des demandes</h1>
        <div className="filter-buttons">
          <button 
            className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('pending')}
          >
            En attente ({requests.filter(r => r.status === 'pending').length})
          </button>
          <button 
            className={`btn ${filter === 'approved' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('approved')}
          >
            Approuvées
          </button>
          <button 
            className={`btn ${filter === 'rejected' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('rejected')}
          >
            Refusées
          </button>
          <button 
            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('all')}
          >
            Toutes
          </button>
        </div>
      </div>

      {alert && (
        <div 
          className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}
          style={{
            position: 'sticky',
            top: '20px',
            zIndex: 1000,
            fontSize: '1.1rem',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            animation: 'slideDown 0.3s ease-out'
          }}
        >
          {alert.message}
        </div>
      )}

      <div className="requests-container">
        <div className="card requests-list-card">
          <h2>Demandes ({requests.length})</h2>
          {requests.length === 0 ? (
            <p>Aucune demande {filter !== 'all' && `avec le statut "${filter}"`}.</p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>QR Code</th>
                    <th>Date</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <React.Fragment key={request.id}>
                      <tr 
                        className={selectedRequest?.id === request.id ? 'selected' : ''}
                        onClick={() => {
                          if (request.status === 'pending') {
                            setSelectedRequest(request);
                            setSelectedItemId('');
                            setQuantity(1);
                            setShowHistory(false);
                            fetchUserScans(request.user_id);
                          }
                        }}
                        style={{ 
                          cursor: request.status === 'pending' ? 'pointer' : 'default',
                          opacity: request.status !== 'pending' ? 0.7 : 1,
                          backgroundColor: request.status === 'approved' ? '#f0f9f0' : 
                                          request.status === 'rejected' ? '#fef0f0' : 'transparent'
                        }}
                      >
                      <td>
                        <strong>{request.user_name}</strong>
                        {request.status === 'approved' && request.approved_item_name && (
                          <div style={{ fontSize: '0.85em', color: '#cbd5e1', marginTop: '0.25rem' }}>
                            ✓ {request.approved_item_name}
                          </div>
                        )}
                        {request.status === 'approved' && request.user_history && request.user_history.length > 0 && (
                          <div style={{ 
                            marginTop: '0.5rem', 
                            padding: '0.5rem', 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: '4px',
                            fontSize: '0.8em'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', color: '#495057' }}>
                              📋 Historique ({request.user_history.length} élément{request.user_history.length > 1 ? 's' : ''})
                            </div>
                            <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                              {request.user_history.slice(0, 5).map((scan, idx) => (
                                <div key={scan.id || idx} style={{ 
                                  padding: '0.25rem 0',
                                  borderBottom: idx < request.user_history.slice(0, 5).length - 1 ? '1px solid #dee2e6' : 'none'
                                }}>
                                  <span style={{ color: '#6c757d' }}>
                                    {new Date(scan.scan_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} - 
                                  </span>
                                  <strong style={{ color: '#495057', marginLeft: '0.25rem' }}>
                                    {scan.item_name}
                                  </strong>
                                  {scan.item_size && (
                                    <span style={{ color: '#6c757d', marginLeft: '0.25rem' }}>
                                      ({scan.item_size})
                                    </span>
                                  )}
                                </div>
                              ))}
                              {request.user_history.length > 5 && (
                                <div style={{ color: '#6c757d', fontStyle: 'italic', marginTop: '0.25rem' }}>
                                  + {request.user_history.length - 5} autre{request.user_history.length - 5 > 1 ? 's' : ''}...
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                      <td><code className="qr-code-display">{request.qr_code}</code></td>
                      <td>{formatDate(request.created_at)}</td>
                      <td>{getStatusBadge(request.status)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {request.status === 'pending' && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setSelectedItemId('');
                              setQuantity(1);
                              setCart([]);
                              setShowHistory(false);
                              fetchUserScans(request.user_id);
                            }}
                            style={{ fontWeight: 'bold' }}
                          >
                            ⚡ Traiter
                          </button>
                        )}
                        {request.status === 'approved' && (
                          <span style={{ color: '#4caf50', fontSize: '1.2em' }}>✅</span>
                        )}
                        {request.status === 'rejected' && (
                          <span style={{ color: '#f44336', fontSize: '1.2em' }}>❌</span>
                        )}
                      </td>
                    </tr>
                    {request.status === 'approved' && request.user_history && request.user_history.length > 0 && (
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <td colSpan="5" style={{ padding: '0.75rem', borderTop: 'none' }}>
                          <div style={{ 
                            padding: '0.75rem', 
                            backgroundColor: 'white', 
                            borderRadius: '4px',
                            border: '1px solid #dee2e6',
                            marginTop: '0.5rem'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: '#495057', fontSize: '0.9em' }}>
                              📋 Historique complet de {request.user_name} ({request.user_history.length} élément{request.user_history.length > 1 ? 's' : ''})
                            </div>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                              gap: '0.75rem',
                              fontSize: '0.85em'
                            }}>
                              {request.user_history.map((scan) => (
                                <div key={scan.id || scan.scan_date} style={{ 
                                  padding: '0.75rem',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '4px',
                                  border: '1px solid #e9ecef'
                                }}>
                                  <div style={{ fontWeight: '500', color: '#495057', marginBottom: '0.25rem' }}>
                                    {scan.item_name}
                                  </div>
                                  <div style={{ fontSize: '0.85em', color: '#6c757d' }}>
                                    📅 {new Date(scan.scan_date).toLocaleDateString('fr-FR', { 
                                      day: '2-digit', 
                                      month: '2-digit', 
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                    {scan.item_size && (
                                      <span style={{ marginLeft: '0.5rem' }}>
                                        • Taille: {scan.item_size}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedRequest && selectedRequest.status === 'pending' && (
          <div className="card request-detail-card" style={{ border: '2px solid #3498db', boxShadow: '0 4px 12px rgba(52, 152, 219, 0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, color: '#2c3e50' }}>⚡ Traiter la demande</h2>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setSelectedRequest(null);
                  setSelectedItemId('');
                  setShowHistory(false);
                }}
                style={{ fontSize: '0.9rem' }}
              >
                ✕ Fermer
              </button>
            </div>

            {/* Informations utilisateur compactes */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontWeight: 'bold', marginBottom: '0.5rem', display: 'block' }}>👤 Nom *</label>
                <input
                  type="text"
                  className="form-control"
                  value={selectedRequest.user_name || ''}
                  onChange={(e) => {
                    const updated = {...selectedRequest, user_name: e.target.value};
                    setSelectedRequest(updated);
                  }}
                  placeholder="Nom de l'utilisateur"
                  style={{ fontSize: '1rem', padding: '0.75rem' }}
                />
                {selectedRequest.user_name && selectedRequest.user_name.startsWith('Utilisateur') && (
                  <small style={{ color: '#856404', display: 'block', marginTop: '0.25rem' }}>⚠️ Première fois - Identifiez l'utilisateur</small>
                )}
              </div>
              
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontWeight: 'bold', marginBottom: '0.5rem', display: 'block' }}>📧 Email (optionnel)</label>
                <input
                  type="email"
                  className="form-control"
                  value={selectedRequest.user_email || ''}
                  onChange={(e) => {
                    const updated = {...selectedRequest, user_email: e.target.value};
                    setSelectedRequest(updated);
                  }}
                  placeholder="email@example.com"
                  style={{ fontSize: '1rem', padding: '0.75rem' }}
                />
              </div>
            </div>

            {/* Historique collapsible */}
            {userScans.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowHistory(!showHistory)}
                  style={{ width: '100%', marginBottom: '0.5rem' }}
                >
                  {showHistory ? '▼' : '▶'} Historique ({userScans.length} scan{userScans.length > 1 ? 's' : ''})
                </button>
                {showHistory && (
                  <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '0.9em' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Élément</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Taille</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userScans.map((scan) => (
                          <tr key={scan.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ padding: '8px' }}>{formatDate(scan.scan_date)}</td>
                            <td style={{ padding: '8px', fontWeight: '500' }}>{scan.item_name}</td>
                            <td style={{ padding: '8px', color: '#cbd5e1' }}>{scan.item_size || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Sélection d'éléments avec panier */}
            <div className="item-selection" style={{ marginTop: '1.5rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>📦 Attribuer des éléments</h3>
              {items.length === 0 ? (
                <div className="alert alert-warning">
                  Aucun élément disponible en stock.
                </div>
              ) : (
                <>
                  {/* Selecteur + bouton ajouter */}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 50px', gap: '0.5rem', marginBottom: '1rem', alignItems: 'end' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '0.5rem', display: 'block' }}>Élément</label>
                      <select
                        className="form-control"
                        value={selectedItemId}
                        onChange={(e) => setSelectedItemId(e.target.value)}
                        style={{ fontSize: '1rem', padding: '0.75rem' }}
                      >
                        <option value="">-- Choisir --</option>
                        {items.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} {item.size ? `(${item.size})` : ''} - Stock: {item.stock_quantity}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '0.5rem', display: 'block' }}>Qté</label>
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        max={(() => {
                          const si = items.find(i => i.id === parseInt(selectedItemId));
                          return si ? si.stock_quantity : 99;
                        })()}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{ fontSize: '1rem', padding: '0.75rem' }}
                      />
                    </div>

                    <button
                      className="btn btn-primary"
                      onClick={addToCart}
                      disabled={!selectedItemId}
                      style={{ padding: '0.75rem', fontSize: '1.5rem', fontWeight: 'bold', height: '48px', marginBottom: '0' }}
                      title="Ajouter au panier"
                    >
                      +
                    </button>
                  </div>

                  {/* Panier */}
                  {cart.length > 0 && (
                    <div style={{ marginBottom: '1.5rem', border: '2px solid #4caf50', borderRadius: '8px', overflow: 'hidden' }}>
                      <div style={{ padding: '0.75rem 1rem', backgroundColor: '#4caf50', color: 'white', fontWeight: 'bold' }}>
                        🛒 Panier ({cart.length} article{cart.length > 1 ? 's' : ''}, {cart.reduce((s, c) => s + c.quantity, 0)} élément{cart.reduce((s, c) => s + c.quantity, 0) > 1 ? 's' : ''})
                      </div>
                      {cart.map((cartItem, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem 1rem',
                          borderBottom: idx < cart.length - 1 ? '1px solid #e0e0e0' : 'none',
                          backgroundColor: idx % 2 === 0 ? '#f8f9fa' : 'white'
                        }}>
                          <div>
                            <strong>{cartItem.item_name}</strong>
                            {cartItem.item_size && <span style={{ color: '#666', marginLeft: '0.5rem' }}>({cartItem.item_size})</span>}
                            <span style={{ marginLeft: '0.75rem', color: '#2196f3', fontWeight: 'bold' }}>×{cartItem.quantity}</span>
                          </div>
                          <button
                            onClick={() => removeFromCart(idx)}
                            style={{
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '0.25rem 0.5rem',
                              cursor: 'pointer',
                              fontSize: '0.9rem'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions principales */}
                  <div className="request-actions" style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                    <button
                      className="btn btn-success"
                      onClick={() => handleApprove(selectedRequest.id)}
                      disabled={(cart.length === 0 && !selectedItemId) || processingRequest}
                      style={{
                        flex: 1,
                        fontSize: '1.1rem',
                        padding: '1rem',
                        fontWeight: 'bold',
                        opacity: ((cart.length === 0 && !selectedItemId) || processingRequest) ? 0.6 : 1
                      }}
                    >
                      {processingRequest ? '⏳ Traitement...' : cart.length > 0
                        ? `✅ Approuver (${cart.length} article${cart.length > 1 ? 's' : ''})`
                        : `✅ Approuver`}
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleReject(selectedRequest.id)}
                      disabled={processingRequest}
                      style={{ padding: '1rem', fontSize: '1rem' }}
                    >
                      ❌ Refuser
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {selectedRequest && selectedRequest.status !== 'pending' && (
          <div className="card request-detail-card">
            <h2>Détails de la demande</h2>
            <div className="request-info">
              <p><strong>Utilisateur:</strong> {selectedRequest.user_name}</p>
              {selectedRequest.user_email && (
                <p><strong>Email:</strong> {selectedRequest.user_email}</p>
              )}
              <p><strong>QR Code scanné:</strong> <code>{selectedRequest.qr_code}</code></p>
              <p><strong>Date de la demande:</strong> {formatDate(selectedRequest.created_at)}</p>
              <p><strong>Statut:</strong> {getStatusBadge(selectedRequest.status)}</p>
              {selectedRequest.approved_item_name && (
                <p><strong>Élément attribué:</strong> {selectedRequest.approved_item_name}</p>
              )}
              {selectedRequest.processed_at && (
                <p><strong>Traité le:</strong> {formatDate(selectedRequest.processed_at)}</p>
              )}
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSelectedRequest(null);
                setSelectedItemId('');
              }}
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestsManagement;

